import { c as _c } from "react/compiler-runtime";
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TweetVideoPlayer } from '@/components/tweet/TweetVideoPlayer';
import { withPhotoSize } from '@/lib/twitter/media-utils';
import { cn } from '@/lib/utils';
import type { UIMediaItem } from '@/types/ui-media';
interface MediaViewerPortalProperties {
  mediaItems: UIMediaItem[];
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  open: boolean;
  resolveVideoUrl: (media: UIMediaItem) => string;
  selectedIndex: number | undefined;
}
export function MediaViewerPortal(properties) {
  const $ = _c(3);
  const documentReference = globalThis.document ?? null;
  if (!properties.open) {
    return null;
  }
  if (!documentReference) {
    return null;
  }
  const contentKey = properties.selectedIndex ?? "default";
  let t0;
  if ($[0] !== contentKey || $[1] !== properties) {
    t0 = createPortal(<MediaViewerContent key={contentKey} {...properties} />, documentReference.body);
    $[0] = contentKey;
    $[1] = properties;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
type MediaViewerContentProperties = Omit<MediaViewerPortalProperties, 'open'>;
function MediaViewerContent(t0) {
  const $ = _c(41);
  const {
    mediaItems,
    selectedIndex,
    onClose,
    onPrev,
    onNext,
    resolveVideoUrl
  } = t0;
  const activeIndex = typeof selectedIndex === "number" ? selectedIndex : 0;
  const activeItem = mediaItems[activeIndex];
  const contentReference = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const preloadedImagesReference = useRef(t1);
  const [hiResLoaded, setHiResLoaded] = useState(false);
  const box = useImageBox(mediaItems, activeIndex);
  let t2;
  let t3;
  if ($[1] !== activeIndex || $[2] !== activeItem || $[3] !== hiResLoaded || $[4] !== mediaItems) {
    t2 = () => {
      if (!(hiResLoaded && activeItem)) {
        return;
      }
      if (!globalThis.document) {
        return;
      }
      const images = [];
      for (const [index, item] of mediaItems.entries()) {
        if (index === activeIndex || item?.type !== "photo" || !item?.url) {
          continue;
        }
        const image = document.createElement("img");
        image.decoding = "async";
        image.loading = "eager";
        image.src = withPhotoSize(item.url, "orig");
        images.push(image);
      }
      preloadedImagesReference.current = images;
      return () => {
        preloadedImagesReference.current = [];
      };
    };
    t3 = [activeIndex, hiResLoaded, mediaItems, activeItem];
    $[1] = activeIndex;
    $[2] = activeItem;
    $[3] = hiResLoaded;
    $[4] = mediaItems;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(_temp, t4);
  let t5;
  if ($[8] !== onClose) {
    t5 = event => {
      const target = event.target;
      if (target instanceof Node && contentReference.current?.contains(target)) {
        return;
      }
      onClose();
    };
    $[8] = onClose;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleOverlayClick = t5;
  let t6;
  if ($[10] !== onClose) {
    t6 = event_0 => {
      const target_0 = event_0.target;
      if (target_0 instanceof Node && contentReference.current?.contains(target_0)) {
        return;
      }
      if (event_0.key === "Enter" || event_0.key === " ") {
        onClose();
      }
    };
    $[10] = onClose;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleOverlayKeyDown = t6;
  let t7;
  if ($[12] !== onClose) {
    t7 = event_1 => {
      event_1.stopPropagation();
      onClose();
    };
    $[12] = onClose;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <X className="h-5 w-5" />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7) {
    t9 = <button type="button" aria-label="Close" className="absolute top-4 right-4 z-layer-modal rounded-full bg-[hsl(var(--overlay-panel-strong))] p-2 text-[hsl(var(--foreground))] shadow-lg ring-1 ring-border/60 backdrop-blur transition-colors hover:bg-[hsl(var(--overlay-panel-muted))]" onClick={t7}>{t8}</button>;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== activeIndex || $[18] !== mediaItems.length || $[19] !== onNext || $[20] !== onPrev) {
    t10 = mediaItems.length > 1 && <>{activeIndex > 0 && <button type="button" aria-label="Previous" className="absolute left-4 top-1/2 z-layer-modal -translate-y-1/2 rounded-full bg-[hsl(var(--overlay-panel-strong))] p-2 text-[hsl(var(--foreground))] shadow-lg ring-1 ring-border/60 backdrop-blur transition-colors hover:bg-[hsl(var(--overlay-panel-muted))]" onClick={event_2 => {
        event_2.stopPropagation();
        onPrev();
      }}><ChevronLeft className="h-6 w-6" /></button>}{activeIndex < mediaItems.length - 1 && <button type="button" aria-label="Next" className="absolute right-4 top-1/2 z-layer-modal -translate-y-1/2 rounded-full bg-[hsl(var(--overlay-panel-strong))] p-2 text-[hsl(var(--foreground))] shadow-lg ring-1 ring-border/60 backdrop-blur transition-colors hover:bg-[hsl(var(--overlay-panel-muted))]" onClick={event_3 => {
        event_3.stopPropagation();
        onNext();
      }}><ChevronRight className="h-6 w-6" /></button>}<div className="absolute bottom-4 left-1/2 z-layer-modal -translate-x-1/2 rounded-full bg-[hsl(var(--overlay-panel-strong))] px-3 py-1 text-sm text-[hsl(var(--foreground))] shadow-lg ring-1 ring-border/60 backdrop-blur">{activeIndex + 1} / {mediaItems.length}</div></>;
    $[17] = activeIndex;
    $[18] = mediaItems.length;
    $[19] = onNext;
    $[20] = onPrev;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== activeItem) {
    t11 = !activeItem && <div className="text-center text-white"><p>Media not available</p><p className="mt-2 text-sm text-muted-foreground">Media URL is missing</p></div>;
    $[22] = activeItem;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== activeItem || $[25] !== resolveVideoUrl) {
    t12 = activeItem && (activeItem.type === "video" || activeItem.type === "animated_gif") && <button type="button" aria-label="Video container" className="relative" style={{
      width: "90vw",
      height: "80vh"
    }} onClick={_temp2}><TweetVideoPlayer src={resolveVideoUrl(activeItem)} playing={true} controls={activeItem.type === "video"} loop={activeItem.type === "animated_gif"} muted={activeItem.type === "animated_gif"} playsInline={true} volume={0.8} width="100%" height="100%" style={{
        objectFit: "cover"
      }} /></button>;
    $[24] = activeItem;
    $[25] = resolveVideoUrl;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== activeItem || $[28] !== box || $[29] !== hiResLoaded) {
    t13 = activeItem && activeItem.type !== "video" && activeItem.type !== "animated_gif" && <button type="button" aria-label="Image container" className="relative flex items-center justify-center overflow-hidden rounded-xl" style={{
      width: box.width || "90vw",
      height: box.height || "80vh"
    }} onClick={_temp3}><Image src={withPhotoSize(activeItem.url || "", "large")} alt={activeItem.altText || "Tweet image"} width={(box.width || activeItem.width || 1920) ?? 1920} height={(box.height || activeItem.height || 1080) ?? 1080} className={cn("max-w-full max-h-full h-auto w-auto rounded-xl object-contain transition-opacity duration-200", hiResLoaded ? "opacity-0" : "opacity-100")} draggable={false} unoptimized={true} /><Image src={withPhotoSize(activeItem.url || "", "orig")} alt={activeItem.altText || "Tweet image"} width={(box.width || activeItem.width || 1920) ?? 1920} height={(box.height || activeItem.height || 1080) ?? 1080} className={cn("absolute inset-0 m-auto max-h-full max-w-full h-auto w-auto rounded-xl object-contain transition-opacity duration-200", hiResLoaded ? "opacity-100" : "opacity-0")} onLoad={() => {
        setHiResLoaded(true);
      }} draggable={false} unoptimized={true} /></button>;
    $[27] = activeItem;
    $[28] = box;
    $[29] = hiResLoaded;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== t13) {
    t14 = <div className="flex items-center justify-center p-8 max-w-full max-h-full" role="presentation" ref={contentReference}>{t11}{t12}{t13}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== handleOverlayClick || $[36] !== handleOverlayKeyDown || $[37] !== t10 || $[38] !== t14 || $[39] !== t9) {
    t15 = <div className="fixed inset-0 z-layer-modal flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={handleOverlayClick} onKeyDown={handleOverlayKeyDown} role="dialog" aria-modal="true" tabIndex={-1}>{t9}{t10}{t14}</div>;
    $[35] = handleOverlayClick;
    $[36] = handleOverlayKeyDown;
    $[37] = t10;
    $[38] = t14;
    $[39] = t9;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
}
function _temp3(event_5) {
  event_5.stopPropagation();
}
function _temp2(event_4) {
  event_4.stopPropagation();
}
function _temp() {
  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  return () => {
    document.body.style.overflow = previousOverflow;
  };
}
function useImageBox(mediaItems, selectedIndex) {
  const $ = _c(8);
  let t0;
  if ($[0] !== mediaItems || $[1] !== selectedIndex) {
    t0 = () => {
      if (globalThis.window === undefined) {
        return {
          width: 0,
          height: 0
        };
      }
      const item = mediaItems[selectedIndex];
      const vw = Math.max(320, Math.floor(window.innerWidth * 0.9));
      const vh = Math.max(240, Math.floor(window.innerHeight * 0.8));
      const iw = item?.width && item.width > 0 ? item.width : 1600;
      const ih = item?.height && item.height > 0 ? item.height : 900;
      const aspect = iw / ih || 1;
      let width = vw;
      let height = Math.round(width / aspect);
      if (height > vh) {
        height = vh;
        width = Math.round(height * aspect);
      }
      return {
        width,
        height
      };
    };
    $[0] = mediaItems;
    $[1] = selectedIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const calculate = t0;
  let t1;
  if ($[3] !== calculate) {
    t1 = () => calculate();
    $[3] = calculate;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [box, setBox] = useState(t1);
  let t2;
  let t3;
  if ($[5] !== calculate) {
    t2 = () => {
      const update = () => {
        setBox(calculate());
      };
      update();
      if (globalThis.window === undefined) {
        return;
      }
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("resize", update);
      };
    };
    t3 = [calculate];
    $[5] = calculate;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  return box;
}